import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, X, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { adminApproveProposal, adminEventProposal, adminEventProposals, adminRejectProposal, errorMessage, imgUrl } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const LABELS = { summary: "Ingress", description: "Omtale", external_url: "Lenkje", contact_email: "Kontakt e-post", contact_phone: "Kontakt telefon" };

/** Live value on the left, proposed on the right – the board sees exactly what changes before saying yes. */
const Compare = ({ k, live, next, liveHtml, nextHtml }) => {
  if (!next) return null;
  const same = (live || "") === next && (liveHtml || "") === (nextHtml || "");
  const Body = ({ text, html, muted }) => html
    ? <div className={cn("article-body no-dropcap rt-lite mt-1.5 !text-[13.5px]", muted && "text-foreground/70")} dangerouslySetInnerHTML={{ __html: html }} />
    : <p className={cn("mt-1.5 whitespace-pre-wrap text-[13.5px] leading-relaxed", muted && "text-foreground/70")}>{text || <em className="text-muted-foreground">ikkje sett</em>}</p>;
  return (
    <div className="grid gap-2 sm:grid-cols-2" data-testid={`proposal-compare-${k}`}>
      <div className="rounded-[10px] border border-border bg-background p-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{LABELS[k]} · no</p>
        <Body text={live} html={liveHtml} muted />
      </div>
      <div className={cn("rounded-[10px] border p-3", same ? "border-border bg-background" : "border-sol/50 bg-[hsl(var(--status-pending-bg))]")}>
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{LABELS[k]} · forslag{same ? " (uendra)" : ""}</p>
        <Body text={next} html={nextHtml} />
      </div>
    </div>
  );
};

const Notices = ({ notices }) => {
  const last = (notices || []).filter((n) => n.kind === "board").slice(-1)[0];
  if (!last || last.outcome === "accepted") return null;
  return <p className="text-[12.5px] text-muted-foreground" data-testid="proposal-notice-outcome">Varsel til styret: {last.outcome === "queued" ? "i kø til kl. 08:00 (nattestopp)" : last.outcome === "skipped" ? "ikkje sendt" : "feila"}{last.error ? ` – ${last.error}` : ""}</p>;
};

const DecideDialog = ({ mode, onOpenChange, onConfirm, loading }) => {
  const [note, setNote] = useState("");
  const reject = mode === "reject";
  return (
    <Dialog open={!!mode} onOpenChange={(o) => !o && onOpenChange(null)}>
      <DialogContent className="bg-card sm:max-w-md" data-testid="proposal-decide-dialog">
        <DialogHeader>
          <DialogTitle>{reject ? "Avvis forslaget" : "Godkjenn og publiser"}</DialogTitle>
          <DialogDescription>{reject ? "Arrangøren får ei melding med grunngjevinga di og kan sende eit nytt forslag." : "Teksten, lenkja og bileta vert lagde på arrangementet med ein gong. Arrangøren får beskjed."}</DialogDescription>
        </DialogHeader>
        <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={reject ? "Kvifor? (obligatorisk)" : "Merknad til arrangøren (valfritt)"} className="bg-background" data-testid="proposal-decide-note" />
        <DialogFooter>
          <Button variant="outline" className="bg-transparent" onClick={() => onOpenChange(null)} data-testid="proposal-decide-cancel">Avbryt</Button>
          <Button variant={reject ? "destructive" : "default"} disabled={loading || (reject && note.trim().length < 5)} onClick={() => onConfirm(note)} data-testid="proposal-decide-confirm">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} {reject ? "Avvis" : "Godkjenn"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** In the event editor: what the organizer has proposed for this event, with approve/reject. */
export const ProposalReview = ({ eventId, onApplied }) => {
  const qc = useQueryClient();
  const [mode, setMode] = useState(null);
  const { data } = useQuery({ queryKey: ["admin-event-proposal", eventId], queryFn: () => adminEventProposal(eventId), enabled: !!eventId });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["admin-event-proposal", eventId] }); qc.invalidateQueries({ queryKey: ["admin-event-proposals"] }); };
  const decide = useMutation({
    mutationFn: ({ m, note }) => (m === "approve" ? adminApproveProposal(data.pending.id, note) : adminRejectProposal(data.pending.id, note)),
    onSuccess: (res, { m }) => { toast.success(m === "approve" ? "Forslaget er godkjent og lagt på arrangementet" : "Forslaget er avvist"); setMode(null); invalidate(); if (m === "approve" && res.event) onApplied?.(res.event); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const p = data?.pending;
  if (!p) return null;
  const f = p.fields || {};
  return (
    <section className="mt-6 rounded-[14px] border border-sol/50 bg-[hsl(var(--status-pending-bg)/0.5)] p-4 sm:p-5" data-testid="proposal-review">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sol">Forslag frå arrangør</p>
          <h3 className="mt-1 text-[16px] font-semibold">{p.organizer_name || "Arrangøren"} har sendt tekst og bilete</h3>
          <p className="mt-0.5 text-[12.5px] text-muted-foreground">Sendt {fmtDateTime(p.updated_at)}{p.revision > 1 ? ` · revisjon ${p.revision}` : ""} · ingenting er publisert enno</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="h-9 bg-transparent" onClick={() => setMode("reject")} data-testid="proposal-reject-btn"><X className="h-4 w-4" /> Avvis</Button>
          <Button className="h-9" onClick={() => setMode("approve")} data-testid="proposal-approve-btn"><Check className="h-4 w-4" /> Godkjenn</Button>
        </div>
      </div>
      {p.note && <p className="mt-3 rounded-[10px] bg-card px-3 py-2 text-[13.5px]" data-testid="proposal-organizer-note">«{p.note}» <span className="text-muted-foreground">– {p.organizer_name}</span></p>}
      <div className="mt-4 space-y-3">
        {Object.keys(LABELS).map((k) => <Compare key={k} k={k} live={data.live?.[k]} next={f[k]} liveHtml={k === "description" ? data.live?.description_html : null} nextHtml={k === "description" ? f.description_html : null} />)}
        {p.images?.length > 0 && (
          <div data-testid="proposal-images-review">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Bilete · {p.images.length} nye</p>
            <ul className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {p.images.map((im, i) => (
                <li key={im.id} className="overflow-hidden rounded-[10px] border border-border bg-card" data-testid={`proposal-review-image-${i}`}>
                  <a href={im.url} target="_blank" rel="noreferrer" className="block aspect-[4/3] bg-secondary"><img src={imgUrl(im.url, 480)} alt="" className="h-full w-full object-cover" /></a>
                  <p className="truncate px-2.5 py-2 text-[12px]">{im.credit ? `Foto: ${im.credit}` : <span className="text-muted-foreground">Utan kreditering</span>}<span className="text-muted-foreground"> · rettar stadfesta</span></p>
                </li>
              ))}
            </ul>
          </div>
        )}
        <Notices notices={p.notices} />
      </div>
      <DecideDialog mode={mode} onOpenChange={setMode} loading={decide.isPending} onConfirm={(note) => decide.mutate({ m: mode, note })} />
    </section>
  );
};

/** Top of Program → Arrangement: every proposal waiting, one tap to open the right event. */
export const PendingProposalsBanner = ({ onOpen }) => {
  const { data } = useQuery({ queryKey: ["admin-event-proposals"], queryFn: () => adminEventProposals("pending"), staleTime: 30_000 });
  if (!data?.length) return null;
  return (
    <div className="mb-5 rounded-[14px] border border-sol/50 bg-[hsl(var(--status-pending-bg)/0.5)] px-4 py-3" data-testid="pending-proposals-banner">
      <p className="text-[13.5px] font-medium">{data.length === 1 ? "Eitt forslag frå arrangør ventar på svar" : `${data.length} forslag frå arrangørar ventar på svar`}</p>
      <ul className="mt-2 flex flex-wrap gap-2">
        {data.map((p) => (
          <li key={p.id}>
            <button type="button" onClick={() => onOpen(p.event_id)} className="inline-flex h-9 items-center gap-1.5 rounded-full border border-border bg-card px-3 text-[13px] font-medium transition-colors hover:border-foreground/40" data-testid={`pending-proposal-${p.id}`}>
              {p.event?.title || p.event_title} <span className="text-muted-foreground">· {p.organizer_name}</span> <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
